import { AnimatePresence, motion } from "framer-motion";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import ChevronDownIcon from "../../../assets/icons/ChevronDownIcon";

const FAQ_KEYS = [
  "free_travel",
  "paid_role",
  "flights",
  "what_gain",
  "who_for",
  "experience_needed",
  "time_commitment",
];

const FAQAnswer = ({ qKey, t }) => {
  const base = `leader_programme.faq.questions.${qKey}`;
  const intro = t(base + ".answer_intro");
  const mid = t(base + ".answer_mid", { defaultValue: "" });
  const items = t(base + ".answer_items", { returnObjects: true, defaultValue: [] });
  const closing = t(base + ".answer_closing", { defaultValue: "" });

  // free_travel has unique paragraph structure
  if (qKey === "free_travel") {
    return (
      <div className="tw-space-y-3">
        <p className="tw-font-semibold">{intro}</p>
        <p>{t(base + ".answer_p1")}</p>
        <p>{t(base + ".answer_p2")}</p>
        <p className="tw-italic tw-text-gray-500">{t(base + ".answer_p3")}</p>
        <p className="tw-font-semibold">{t(base + ".answer_p4")}</p>
      </div>
    );
  }

  return (
    <div className="tw-space-y-3">
      <p>{intro}</p>
      {mid && <p className="tw-font-semibold">{mid}</p>}
      {Array.isArray(items) && items.length > 0 && (
        <ul className="tw-list-disc tw-space-y-1 tw-pl-5">
          {items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      )}
      {closing && <p className="tw-italic tw-text-gray-500">{closing}</p>}
    </div>
  );
};

const LeaderFAQ = () => {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="tw-bg-white tw-px-4 tw-py-10 md:tw-px-16 md:tw-py-16 lg:tw-px-24">
      <div className="tw-text-center tw-text-[26px] tw-font-extrabold md:tw-text-[32px]">
        {t("leader_programme.faq.title")}
      </div>
      <div className="tw-mb-6 tw-text-center tw-text-base tw-font-semibold tw-text-primary-main md:tw-mb-10 md:tw-text-lg">
        {t("leader_programme.faq.subtitle")}
      </div>
      <div className="md:tw-mx-auto md:tw-max-w-3xl">
        {FAQ_KEYS.map((qKey, index) => (
          <div
            key={qKey}
            className={`tw-border-x tw-border-b tw-border-[#E9ECEF] ${
              index === FAQ_KEYS.length - 1 ? "tw-rounded-bl-lg tw-rounded-br-lg" : ""
            } ${index === 0 ? "tw-rounded-tl-lg tw-rounded-tr-lg tw-border-t" : ""}`}
          >
            <button
              onClick={() => toggleFAQ(index)}
              className={`tw-flex tw-w-full tw-items-center tw-gap-2 tw-px-4 tw-py-3 tw-transition-colors hover:tw-bg-[#F8F9FA] md:tw-px-6 md:tw-py-4 ${
                openIndex === index ? "tw-bg-[#F8F9FA]" : ""
              }`}
            >
              <div className="tw-grow tw-text-left tw-text-sm tw-font-medium md:tw-text-base">
                {t(`leader_programme.faq.questions.${qKey}.question`)}
              </div>
              <motion.div
                animate={{ rotate: openIndex === index ? 180 : 0 }}
                transition={{ duration: 0.3 }}
                className="tw-flex-none tw-text-[#CED4DA]"
              >
                <ChevronDownIcon className="tw-h-5 tw-w-5 md:tw-h-6 md:tw-w-6" />
              </motion.div>
            </button>
            <AnimatePresence>
              {openIndex === index && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="tw-overflow-hidden"
                >
                  <div className="tw-border-t tw-border-[#E9ECEF] tw-bg-[#F8F9FA] tw-px-4 tw-py-3 tw-text-sm md:tw-px-6 md:tw-py-4 md:tw-text-base">
                    <FAQAnswer qKey={qKey} t={t} />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>
    </section>
  );
};

export default LeaderFAQ;
